/** Must match backend BREAK_LIMIT_MINUTES */
export const BREAK_LIMITS = { 1: 15, 2: 35, 3: 10 };

export const IST_TIME_ZONE = 'Asia/Kolkata';

export const formatISTDate = (value) =>
  new Date(value).toLocaleDateString('en-IN', {
    timeZone: IST_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export const formatISTDateKey = (value) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(value));

export const formatISTTime12h = (value) =>
  new Date(value).toLocaleTimeString('en-IN', {
    timeZone: IST_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export const formatIST24HourTime = (value) =>
  new Date(value).toLocaleTimeString('en-GB', {
    timeZone: IST_TIME_ZONE,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

export function getTodayISTKey() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function findTodayRecord(attendanceList) {
  const key = getTodayISTKey();
  return (attendanceList || []).find((r) => formatISTDateKey(r.date) === key) || null;
}

/**
 * Prefer the open session row when the user is checked in (night shift can use a
 * different IST calendar `date` than "today"). Otherwise use today's list row.
 */
export function resolveTodayView(attendanceList, session) {
  const open = session && session.checkIn && !session.checkOut;
  if (open && session._id) {
    const sid = session._id.toString();
    const fromList = (attendanceList || []).find((r) => r._id && r._id.toString() === sid);
    return fromList || session;
  }
  return findTodayRecord(attendanceList);
}

export function totalBreakMinutes(record) {
  if (!record?.breaks?.length) return 0;
  return record.breaks.reduce((sum, b) => sum + (Number(b.durationMinutes) || 0), 0);
}

/**
 * Hours for summary: finalized `workHours` after checkout; before checkout, net elapsed
 * minus completed breaks and the current active break (wall clock).
 */
export function getDisplayWorkHoursToday(record) {
  if (!record?.checkIn) return null;
  if (record.checkOut != null && record.workHours != null && !Number.isNaN(Number(record.workHours))) {
    return Number(record.workHours);
  }
  const inMs = new Date(record.checkIn).getTime();
  const outMs = Date.now();
  let workedMs = outMs - inMs;
  const DAY_MS = 24 * 60 * 60 * 1000;
  if (workedMs < 0) workedMs += DAY_MS;
  if (workedMs > 2 * DAY_MS) workedMs = DAY_MS;

  let breakMs = totalBreakMinutes(record) * 60 * 1000;
  if (record.activeBreak?.startedAt) {
    breakMs += Math.max(0, Date.now() - new Date(record.activeBreak.startedAt).getTime());
  }
  const netMs = Math.max(0, workedMs - breakMs);
  return Number((netMs / (60 * 60 * 1000)).toFixed(2));
}

export function formatBreakSummary(b) {
  let summary;
  if (!b.lateEnd) {
    summary = `#${b.number}: ${b.durationMinutes}m`;
  } else {
    const actual = b.actualDurationMinutes ?? b.durationMinutes;
    const limit = BREAK_LIMITS[b.number];
    const overMins = Math.max(0, actual - limit);
    summary = `#${b.number}: ${actual}m actual, ${b.durationMinutes}m counted (+${overMins}m over)`;
  }
  if (b.comment) {
    summary += ` — ${b.comment}`;
  }
  return summary;
}

export function isActiveBreakOverLimit(activeBreak) {
  if (!activeBreak?.startedAt || !activeBreak?.number) return false;
  const limitMs = (BREAK_LIMITS[activeBreak.number] || 0) * 60 * 1000;
  if (!limitMs) return false;
  const elapsedMs = Date.now() - new Date(activeBreak.startedAt).getTime();
  return elapsedMs > limitMs;
}
